import type { LandingPageContent } from './types'

export const SOLARANLAGE_KOSTEN: LandingPageContent = {
  slug: 'solaranlage-kosten',
  lang: 'de',
  topicId: 'solaranlage-kosten',
  title: 'Solaranlage Kosten – Preise & kostenloser Vergleich',
  metaDescription:
    'Was kostet eine Photovoltaikanlage wirklich? Erfahren Sie die Preisfaktoren und vergleichen ' +
    'Sie kostenlos Angebote von Handwerksbetrieben in Ihrer Region.',
  keywords: [
    'Solaranlage Kosten',
    'Photovoltaik Kosten',
    'PV-Anlage Preis',
    'Was kostet eine Solaranlage',
    'Solaranlage Kosten Rechner',
    'Photovoltaik Preisunterschied Handwerker',
    // Aus der Keyword-Recherche für /pv-kostenrechner (05-seo-landingpages.md) - beide Themen
    // werden im Fließtext oben bereits gestreift (Speicherwahl, regionale Preisunterschiede),
    // bisher aber nicht als Keyword erfasst.
    'Batteriespeicher Kosten pro kWh',
    'Photovoltaik Preisvergleich regional',
    // SEO-4.0-Story Task 07 (docs/tasks/active/seo/seo-4.0/07-verbrauchsstarke-nischen-nachts-autark.md) -
    // stromintensive Nutzungsszenarien, unterschiedlich stark bewertet, siehe Task-Dokument.
    'Klimaanlage Photovoltaik',
    'Gaming PC Stromverbrauch Photovoltaik',
    'PV Überschuss Mining',
  ],
  eyebrow: 'Kostenübersicht',
  h1: 'Was kostet eine Solaranlage? Preise, Einflussfaktoren & Vergleich',
  intro:
    'Die Kosten einer Photovoltaikanlage schwanken stark - je nach Dachgröße, Anlagenleistung, ' +
    'Speicher und Region können mehrere Tausend Euro Unterschied zwischen zwei Angeboten für ' +
    'dasselbe Dach liegen. Hier erfahren Sie, welche Faktoren den Preis wirklich bestimmen und wie ' +
    'Sie einen fairen Marktpreis für Ihr Projekt finden.',
  sections: [
    { type: 'heading', text: 'Warum eine pauschale Preisangabe wenig hilft' },
    {
      type: 'paragraph',
      text:
        'Ein fixer Preis pro kWp klingt einfach, ignoriert aber Dachzuschnitt, Gerüstaufwand, ' +
        'Speicherwahl und regionale Handwerkerpreise. Die Gesamtkosten ergeben sich erst aus dem ' +
        'Zusammenspiel mehrerer Faktoren - deshalb schwanken Angebote für dasselbe Dach in der ' +
        'Praxis oft erheblich.',
    },
    { type: 'heading', text: 'Diese Faktoren beeinflussen den Preis' },
    {
      type: 'list',
      items: [
        'Anlagengröße (kWp) und verfügbare Dachfläche',
        'Dachausrichtung und -neigung',
        'Batteriespeicher ja/nein und Kapazität',
        'Zusätzlicher Bedarf durch Wärmepumpe oder E-Auto',
        'Zustand des Zählerschranks und nötige Elektroarbeiten',
        'Gerüst- und Montageaufwand je nach Dachtyp',
        'Regionale Preisunterschiede zwischen Handwerksbetrieben',
      ],
    },
    { type: 'heading', text: 'Warum Angebote für dasselbe Dach stark variieren können' },
    {
      type: 'paragraph',
      text:
        'Auslastung, Marge und Erfahrung unterscheiden sich von Betrieb zu Betrieb - wer nur ein ' +
        'einziges Angebot einholt, hat keinen Vergleichswert und kann schwer beurteilen, ob der ' +
        'Preis fair ist. Erst mehrere Angebote für dieselbe Anfrage zeigen, wo der tatsächliche ' +
        'Marktpreis liegt.',
    },
    { type: 'heading', text: 'So finden Sie den besten Preis - ohne Kaltakquise' },
    {
      type: 'paragraph',
      text:
        'Bei Roofora stellen Sie eine anonymisierte Anfrage: Dachbilder, Zählerschrank-Foto, ' +
        'Ausrichtung und Verbrauch reichen aus. Handwerksbetriebe geben darauf unverbindliche ' +
        'Festpreis-Angebote ab, die Sie in Ruhe sortiert nach Preis vergleichen. Kontakt entsteht ' +
        'erst, wenn Sie einem einzelnen Betrieb aktiv zustimmen - keine Sammelfreigabe, keine ' +
        'Anruf-Flut.',
    },
  ],
  faq: [
    {
      q: 'Lohnt sich ein Batteriespeicher preislich?',
      a: 'Das hängt stark von Ihrem Verbrauchsprofil ab - etwa ob Sie tagsüber viel Strom nutzen oder eher abends. Handwerksbetriebe können das anhand Ihrer Angaben zu Verbrauch, Wärmepumpe und E-Auto realistisch einschätzen.',
    },
    {
      q: 'Warum unterscheiden sich Angebote für dasselbe Dach so stark?',
      a: 'Weil Auslastung, Materialwahl und Kalkulation von Betrieb zu Betrieb variieren. Genau deshalb lohnt sich ein Vergleich mehrerer unabhängiger Angebote statt nur eines.',
    },
    {
      q: 'Kostet die Nutzung von Roofora etwas?',
      a: 'Nein - eine Anfrage zu stellen, Angebote zu erhalten und zu vergleichen ist für Sie als Kunde komplett kostenlos.',
    },
    {
      q: 'Reicht meine Anlage auch für eine Klimaanlage im Sommer?',
      a: 'Klimaanlagen laufen meist tagsüber - genau dann, wenn eine PV-Anlage am meisten Strom liefert. Externe Quellen nennen das als eine der wirtschaftlich sinnvollsten Kombinationen. Wie groß Ihre Anlage dafür ausgelegt sein sollte, hängt vom Gerätetyp und Ihrem übrigen Verbrauch ab - geben Sie den zusätzlichen Bedarf beim PV-Rechner mit an.',
    },
    {
      q: 'Ändert ein Gaming-PC etwas an der sinnvollen Anlagengröße?',
      a: 'Ein Gaming-PC kann je nach Ausstattung mehrere hundert Watt ziehen, vor allem bei langer Nutzung spürbar mehr Jahresverbrauch. Roofora rechnet dafür keinen eigenen Aufschlag - geben Sie Ihren tatsächlichen Jahresverbrauch inklusive Gaming-Nutzung im PV-Rechner an, damit die Anlagengröße dazu passt.',
    },
    {
      q: 'Kann ich PV-Überschuss für Krypto-Mining nutzen?',
      a: 'Technisch ja - überschüssiger Solarstrom, der sonst für wenig Geld eingespeist würde, lässt sich stattdessen für Mining-Hardware nutzen. Das ist aber ein Sonderfall ohne eigene Berechnung in unserem Rechner - die Wirtschaftlichkeit hängt stark von Ihrer Hardware und den aktuellen Rahmenbedingungen ab und sollte gesondert kalkuliert werden.',
    },
  ],
  primaryCta: { label: 'Jetzt kostenlose Anlagengröße berechnen', action: 'link', href: '/pv-rechner' },
  secondaryCta: { label: 'Direkt Anfrage erstellen', action: 'customer' },
  relatedLinks: [
    // Verlinkung statt Duplizierung (05-seo-landingpages.md, "/solaranlage-kosten erweitern statt
    // duplizieren") - der Kostenrechner selbst wird hier bewusst nicht eingebettet (kein
    // Widget-Slot im generischen SeoLandingPage-Renderer), nur klar dorthin verlinkt.
    { label: 'PV-Kostenrechner: Durchschnittspreis für Ihre Anlage berechnen', href: '/pv-kostenrechner' },
    { label: 'So funktioniert der Angebotsvergleich', href: '/pv-angebot-vergleichen' },
    { label: 'PV-Installateur finden', href: '/pv-installateur-finden' },
    { label: 'Photovoltaik Schritt für Schritt planen', href: '/photovoltaik-planen' },
    { label: 'Photovoltaik-Förderung 2026: Deutschland & Österreich', href: '/blog/photovoltaik-foerderung-2026' },
    { label: 'Angebote anfragen ganz ohne Telefonnummer oder E-Mail', href: '/photovoltaik-ohne-telefonnummer' },
    { label: 'Photovoltaik und Wärmepumpe kombinieren', href: '/photovoltaik-waermepumpe' },
    { label: 'Wallbox & Elektroauto mit PV-Überschuss laden', href: '/photovoltaik-wallbox-elektroauto' },
    { label: 'Photovoltaik-Förderung: KfW, Einspeisevergütung & Zuschüsse', href: '/photovoltaik-foerderung' },
  ],
}
